/**
 * « Mise en route » (idée du propriétaire du 18/10/2026) : sur l'accueil de qui
 * règle l'entreprise, la liste courte des réglages indispensables, calculée par
 * le serveur (`GET /onboarding/company`), chaque ligne ouvrant sa rubrique de
 * « Mon entreprise ». Elle ne bloque rien : la carte disparaît quand tout est
 * fait et se masque pour ce compte. Elle remplace « Bien démarrer » et l'ancien
 * choix du métier en fenêtre (rubrique « Vos métiers »).
 *
 * L'accueil n'en montre que la progression (« 3 sur 7 ») et « Continuer » :
 * la liste des étapes restantes est sur sa page, `/mise-en-route`.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CompanySetupItem } from '@myequip/shared';
import { api } from '../../core/api.js';
import { useAuth, useCan } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { Checklist, type ChecklistItem } from '../../components/ui/Checklist.js';
import { Section } from '../../components/ui/Section.js';
import { HubPage } from '../../components/templates/templates.js';
import { EmptyState } from '../../components/ui.js';
import { QueryBoundary } from '../../components/ui/Feedback.js';
import { rememberOnboarding } from './Welcome.js';
import { Banner } from '../../components/ui/Feedback.js';

export interface CompanySetup {
  items: CompanySetupItem[];
  done: number;
  total: number;
  dismissed: boolean;
  visible: boolean;
}

export const COMPANY_SETUP_QUERY_KEY = 'company-setup';

/** État de la mise en route, seulement pour qui règle l'entreprise (sinon 403). */
export function useCompanySetup(enabled: boolean) {
  const { me } = useAuth();
  return useQuery({
    queryKey: [COMPANY_SETUP_QUERY_KEY, me?.id],
    queryFn: () => api<CompanySetup>('/onboarding/company'),
    enabled: enabled && Boolean(me && !me.support_session),
    // Relue au retour de « Mon entreprise » : une étape réglée se coche dès l'accueil rouvert.
    staleTime: 5_000,
    retry: false,
  });
}

export const COMPANY_SETUP_PATH = '/mise-en-route';

/** Masquer la mise en route pour ce compte : retenu côté serveur, la carte disparaît partout. */
export function useHideCompanySetup(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await rememberOnboarding('mise_en_route_masquee');
    await queryClient.invalidateQueries({ queryKey: [COMPANY_SETUP_QUERY_KEY] });
  };
}

/** Les étapes de la liste : chaque ligne ouvre sa rubrique de « Mon entreprise ». */
function useSetupItems(setup: CompanySetup, from: string): ChecklistItem[] {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: [COMPANY_SETUP_QUERY_KEY] });
  return setup.items.map((item) => ({
    id: item.key,
    label: t(`miseEnRoute.etapes.${item.key}.titre`),
    hint: t(`miseEnRoute.etapes.${item.key}.aide`),
    done: item.done,
    // Ouvrir la rubrique des rôles depuis la liste vaut vérification (pour toute l'entreprise).
    ...(item.key === 'roles'
      ? {
          onOpen: () => {
            void api('/onboarding/company/roles-reviewed', { method: 'POST' })
              .then(refresh, () => undefined);
            navigate(item.to, { state: { from } });
          },
        }
      : { to: item.to }),
  }));
}

function progressProps(setup: CompanySetup) {
  return {
    label: t('miseEnRoute.titre'),
    progress: t('miseEnRoute.progression', { fait: setup.done, total: setup.total }),
    progressLabel: t('miseEnRoute.progressionAria', { fait: setup.done, total: setup.total }),
    doneLabel: t('miseEnRoute.fait'),
  };
}

/** Carte de l'accueil : la progression et « Continuer » vers la page de la liste. */
export function CompanySetupCard(): ReactNode {
  const can = useCan();
  const setup = useCompanySetup(can('settings.manage'));
  const hide = useHideCompanySetup();
  const [hidden, setHidden] = useState(false);
  // Rien n'est affirmé avant la réponse : ni pendant le chargement, ni après un échec.
  if (hidden || !setup.data?.visible) return null;
  return (
    <Section
      title={t('miseEnRoute.titre')}
      action={{ label: t('miseEnRoute.masquer'), onClick: () => { setHidden(true); void hide(); } }}
    >
      <Checklist
        {...progressProps(setup.data)}
        items={setup.data.items.map((item) => ({ id: item.key, label: t(`miseEnRoute.etapes.${item.key}.titre`), done: item.done }))}
        compact
        action={{ label: t('miseEnRoute.continuer'), to: COMPANY_SETUP_PATH, state: { from: '/' } }}
      />
    </Section>
  );
}

/** Page de la mise en route : les étapes restantes (les faites sont masquées). */
export function CompanySetupPage(): ReactNode {
  const setup = useCompanySetup(true);
  const navigate = useNavigate();
  const hide = useHideCompanySetup();
  const [hideFailed, setHideFailed] = useState(false);
  const items = useSetupItems(setup.data ?? { items: [], done: 0, total: 0, dismissed: false, visible: false }, COMPANY_SETUP_PATH);
  return (
    <HubPage
      header={{
        title: t('miseEnRoute.titre'),
        description: t('miseEnRoute.aide'),
        backTo: '/',
        menu: [{ label: t('miseEnRoute.masquer'), onSelect: () => { setHideFailed(false); hide().then(() => navigate('/'), () => setHideFailed(true)); } }],
      }}
    >
      {hideFailed ? <Banner tone="danger">{t('miseEnRoute.masquerEchec')}</Banner> : null}
      <QueryBoundary
        query={setup}
        isEmpty={(value) => !value.visible}
        empty={
          <EmptyState>
            {setup.data && setup.data.dismissed && setup.data.done < setup.data.total
              ? t('miseEnRoute.masquee')
              : t('miseEnRoute.toutFait')}
          </EmptyState>
        }
      >
        {(value) => <Checklist {...progressProps(value)} items={items} hideDone />}
      </QueryBoundary>
    </HubPage>
  );
}
